/**
 * WP1 (Auftrags-Dashboard, 08.09.2026) — reine Ableitung des Auftrags-Verlaufs aus drei
 * bestehenden Quellen, OHNE neue Route: `GET /api/events/:id/activity` (Statuswechsel/Ereignisse,
 * limitiert auf die letzten 200 Zeilen — s. Regel „Altdaten ergänzen" unten), `GET
 * /api/events/:id/offer-history` (jede gesendete Angebotsversion mit vollständigem
 * Positions-Snapshot) und der bestehende Mail-Feed (`GET /api/events/:id/emails`).
 *
 * Ergebnis speist `ui/auftrag/AuftragVerlaufKarte.tsx`, die in `pages/event-detail.tsx` die drei
 * Karten „Aktivität", „Versionsarchiv" und „Vorgangsansicht" ERSETZT (eine Wahrheit statt drei
 * Listen, die je einen Teil derselben Geschichte erzählten).
 *
 * ── Antoines Screenshot-Beleg (07.09.2026) + die drei verbindlichen Regeln daraus ─────────────
 * Im echten „Aktivität"-Bereich erschienen fuer DENSELBEN Versand ZWEI Zeilen (eine E-Mail-Karte +
 * eine separate `offer.sent`-Statuszeile mit eigenem Zeitstempel), dazu uneinheitliche
 * Zeitstempel (mal nur Datum, mal Datum+Uhrzeit) und dreifach hintereinander derselbe
 * unübersetzte Code `offer.title_or_guest_count_changed` (drei technische Einzel-Schreibvorgänge
 * EINES Speichern-Klicks). Diese Funktion setzt deshalb verbindlich durch:
 *
 *  1. JEDE Zeile trägt einen vollständigen Zeitstempel (Datum + Uhrzeit) — nie nur ein Datum.
 *     Durchgesetzt dadurch, dass `zeitpunkt` ausschließlich aus `timestamptz`-Spalten kommt
 *     (`activity_log.created_at` / `offer_history.sent_at` / `emails.received_at`), NIE aus
 *     `events.event_date` (das wäre ein Datum ohne Uhrzeit). Das eigentliche Formatieren
 *     ("TT.MM.JJJJ · HH:MM Uhr", überall gleich) übernimmt die Karte, nicht diese Datei — eine
 *     Zeile ohne `zeitpunkt` wird hier erst gar nicht erzeugt.
 *  2. Eine gesendete/erneut gesendete Version besteht aus GENAU EINER Zeile, auch wenn dieselbe
 *     Aktion sowohl im Activity-Log als auch im Mail-Feed auftaucht. Schlüssel ist die
 *     VERSIONSNUMMER, nicht eine `emailId` — die es als verlässliche Fremdschlüssel-Verknüpfung
 *     nicht gibt (`emails` trägt keinen Bezug auf eine Angebotsfassung, s.
 *     `packages/angebots-verlauf/src/index.ts` § "mail_gesendet": dort bewusst GETRENNT gehalten,
 *     weil eine Adresse an eine Version zu hängen dort eine Verknüpfung behauptet hätte, die in
 *     den Daten nicht steht — und das bleibt für DIESE Datei genauso wahr). Diese Karte löst
 *     genau die sichtbare Dopplung, die aus dieser fehlenden Verknüpfung entstand, deshalb über
 *     einen Näherungs-Abgleich per Zeitnähe statt eines erfundenen Fremdschlüssels — s.
 *     `ordneMailsDenVersionenZu`: eine ausgehende Mail, die zeitlich zu einer Version passt, WIRD
 *     Teil von deren Zeile (Empfängeradresse als Zusatzinfo); ohne Treffer bleibt sie eine eigene
 *     `mail`-Zeile. Für alle anderen Aktionscodes (Freitext, Klärpunkt-Fragen, …) bleibt die
 *     bisherige Trennung bestehen — dafür fordert Antoines Beleg keinen Zusammenschluss.
 *  3. Mehrere technische Einzel-Ereignisse EINER Nutzeraktion (gleicher Aktionscode, gleiche
 *     betroffene Zeile laut `details`, Zeitabstand <= 60s zum jeweils vorherigen in der Kette)
 *     werden zu EINER Zeile zusammengefasst — der letzte Stand zählt, ältere Zwischenstände
 *     werden beim Zusammenfassen verworfen, nicht einzeln angezeigt. Ohne erkennbare
 *     Datensatz-ID im `details`-Payload wird NIE zusammengefasst (konservativ: lieber zwei
 *     Zeilen als eine falsche Zusammenfassung verschiedener Datensätze).
 *
 * ── A4 (13.09.2026, `docs/PLAN-VORGANGSSEITE-UMBAU.md` Einheit A) ──────────────────────────────
 * Neu hinzugekommen: die Bezugs-IDs aus `activity_log.details` (`bezugsIds`) und die reine
 * Zuordnung Zeile -> Sprungziel (`verlaufZiel`). Bis dahin war nur die Angebotsversion klickbar;
 * Rechnungen, Zahlungen, Kostenuebernahmen und Nachrichten-Zuordnungen endeten als Sackgasse,
 * obwohl die verbindende ID im Payload stand.
 *
 * Getestet in `apps/web/test/auftrag-verlauf.test.ts`.
 */
import { ACTIVITY_LABEL, AENDERUNGS_ANLASS_TEXT, rechnungNichtErstelltText, KOSTENUEBERNAHME_ENTSCHEIDUNG_LABEL, type KostenuebernahmeEntscheidung } from "./activity-labels";
import { abrechnungPfad, posteingangPfad } from "./pfade";
import { eventStatusMeta } from "../ui/format";
import { zahlungEingegangenText } from "./zahlungsstand";

export type AuftragVerlaufArt =
  | "anfrage"
  | "version"
  | "aenderungswunsch"
  | "annahme"
  | "ablehnung"
  | "rechnung"
  | "zahlung"
  | "kostenuebernahme"
  | "mail"
  | "status"
  /** PR4 (`docs/PLAN-EVENT-FELD-VERSIONIERUNG.md` Abschnitt 4.1 Punkt 3): eine verdichtete
   *  Sammelzeile fuer Eckdaten-Aenderungen (Regel C, `lib/feld-versionen.ts::buendleFuerVerlaufskarte`)
   *  — entsteht NICHT in `leiteAuftragsVerlaufAb` selbst (die kennt `event_field_versions` nicht),
   *  sondern wird vom Aufrufer (`pages/event-detail.tsx`) separat gebaut und in die sortierte Liste
   *  eingemischt. Oeffnet `FeldVersionenDrawer`, kein eigenes Sprungziel (`verlaufZiel` unten). */
  | "eckdaten";

export interface AuftragVerlaufEintrag {
  /** Stabiler React-Key + fachlicher Dedupe-Schlüssel (`version-3`, `activity-<id>`, `mail-<id>`). */
  key: string;
  art: AuftragVerlaufArt;
  /** ISO-Zeitstempel MIT Uhrzeit — Formatierung übernimmt die Karte (Kriterium 1). */
  zeitpunkt: string;
  /** Fertig formulierter Titel der Zeile (aus `ACTIVITY_LABEL` bzw. eigener Text je Art). */
  titel: string;
  /** Zusatzzeile — Empfängeradresse, Notiz, Betreff o. ä. Ohne Inhalt bewusst `undefined`. */
  detail?: string;
  icon: string;
  /** Erfolgs-Zeilen (Zahlung eingegangen, vollständig bezahlt) zeichnet die Karte grün. */
  erfolg?: boolean;
  version?: number;
  /** Nur bei `art: "aenderungswunsch"`, wenn eine spätere Version gefunden wurde. */
  folgeVersion?: number;
  /** Nur bei `art: "version"` — spiegelt `VersionVorschauDrawer`s `vollstaendig`-Prüfung. */
  hatPositionen?: boolean;
  /**
   * Nur bei `art: "mail"` (eine Mail, die KEINER Version zugeordnet wurde, s. Kriterium 2): die
   * `emails.id` der zugrundeliegenden Zeile. Die Karte nutzt sie, um bei Bedarf die volle
   * `EmailRow` nachzuschlagen (Zustellstatus, Fehlertext, Retry-Bündelung — Bughunt 28.08.2026,
   * `test/mail-aktivitaet-verdichtung.test.tsx`) — diese reine Funktion selbst formuliert dafür
   * bewusst nur einen einfachen Fallback-Text (`titel`/`detail` unten), keine Zustellstatus-Logik.
   */
  emailId?: string;
  /**
   * ── A4 (Antoine, 13.09.2026, `docs/PLAN-VORGANGSSEITE-UMBAU.md` Einheit A) ──────────────────
   * Bis hierher hat `leiteAuftragsVerlaufAb` `activity_log.details` komplett verworfen: aus einer
   * Zeile wurden nur Titel, Icon und Zeitpunkt. Die IDs, die den Eintrag mit dem dazugehoerigen
   * Datensatz verbinden, liegen dort aber seit jeher vor — `routes/invoices.ts` schreibt
   * `invoice_id`, `routes/payments.ts` `paymentId`, `routes/messages.ts` `messageId`,
   * `routes/cost-acceptance.ts` `cost_acceptance_id`. Sie werden jetzt uebernommen, damit die
   * Karte daraus ECHTE Links bauen kann (s. `verlaufZiel` unten). Keine neue Route noetig.
   *
   * Bewusst je ein eigenes Feld statt einer generischen `entityId`: das Ziel haengt an der ART
   * des Datensatzes, nicht daran, dass „irgendeine ID" vorhanden ist. Fehlt die ID (aeltere
   * Zeilen, Aktionen ohne Bezug), bleibt das Feld `undefined` — der Eintrag ist dann nicht
   * klickbar, statt auf ein geratenes Ziel zu fuehren (kein toter Klick).
   */
  invoiceId?: string;
  paymentId?: string;
  messageId?: string;
  costAcceptanceId?: string;
}

/**
 * Das Sprungziel einer Verlaufs-Zeile — oder `null`, wenn es keines gibt.
 *
 * `href` ist bewusst eine URL und kein Callback: die Karte rendert damit ein echtes `<Link to=…>`,
 * damit Cmd-/Strg-/Mittelklick einen neuen Tab oeffnet (ausdrueckliche Forderung von Antoine,
 * 13.09.2026 — ein `onClick`+`navigate` kann das grundsaetzlich nicht). Versions-Zeilen haben
 * bewusst KEIN Ziel hier: sie oeffnen den bestehenden `VersionVorschauDrawer` auf derselben Seite,
 * fuer den es keine eigene Adresse gibt.
 */
export interface AuftragVerlaufZiel {
  href: string;
  /** Vorlesetext fuer das `aria-label` — sagt, WOHIN der Klick fuehrt. */
  label: string;
}

/**
 * Eingabetypen — strukturell kompatibel mit den bestehenden Typen in `pages/event-detail.tsx`
 * (`ActivityEntry`, `EmailRow`) bzw. `ui/auftrag/VersionVorschauDrawer.tsx`
 * (`OfferHistoryEntry`), aber bewusst hier EIGENSTÄNDIG deklariert: ein `lib/*`-Modul importiert
 * keine Seiten-/Komponenten-Typen (Grundregel für reine Funktionen in diesem Repo). Die
 * Aufrufer-Objekte erfüllen diese Formen ohnehin schon strukturell — TypeScript prüft das ohne
 * Konvertierung.
 */
export interface AuftragVerlaufActivityEntry {
  id: string;
  actorType: "user" | "customer" | "system";
  actorLabel: string | null;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
}

export interface AuftragVerlaufHistoryEntry {
  id: string;
  version: number;
  sentAt: string | null;
  optionsSnapshot: { items?: unknown[]; days?: unknown[] }[];
}

export interface AuftragVerlaufEmailEntry {
  id: string;
  direction: "inbound" | "outbound";
  fromAddr: string;
  toAddr: string | null;
  subject: string | null;
  receivedAt: string;
}

/** Kriterium 3: Zeitabstand, innerhalb dessen Einzel-Events derselben Aktion/Zeile verschmelzen. */
const BURST_FENSTER_MS = 60_000;
/** Kriterium 2: Toleranz für den Näherungs-Abgleich Mail↔Version (`ordneMailsDenVersionenZu`). Die
 *  E-Mail-Zeile entsteht im selben Request wie `logActivity("offer.sent"/"offer.resent", …)`
 *  (`routes/offers.ts`) — 10 Minuten sind großzügig genug für Warteschlangen-/Zustellverzögerung,
 *  ohne eine spätere, unabhängige Mail versehentlich einzufangen. */
const MAIL_ZUORDNUNGS_FENSTER_MS = 10 * 60_000;

function zeitMs(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** `details.optionId`/`.itemId`/… — die betroffene Datensatz-ID, sofern im Payload vorhanden. */
function entityId(details: Record<string, unknown> | null | undefined): string | null {
  if (!details) return null;
  for (const feld of ["optionId", "itemId", "invoiceId", "paymentId", "entityId", "id"] as const) {
    const wert = details[feld];
    if (typeof wert === "string" && wert.length > 0) return wert;
  }
  return null;
}

/** Ein String-Feld aus `details`, sonst `undefined` — Zahlen/Objekte/leere Strings zaehlen nicht. */
function detailId(details: Record<string, unknown> | null | undefined, ...felder: string[]): string | undefined {
  if (!details) return undefined;
  for (const feld of felder) {
    const wert = details[feld];
    if (typeof wert === "string" && wert.length > 0) return wert;
  }
  return undefined;
}

/**
 * A4: die Bezugs-IDs einer Activity-Zeile. Die Schreibweise ist im Bestand uneinheitlich
 * (`invoice_id`/`cost_acceptance_id` in snake_case aus `routes/invoices.ts`/`cost-acceptance.ts`,
 * `paymentId`/`messageId` in camelCase aus `routes/payments.ts`/`messages.ts`) — beide Formen
 * werden hier gelesen, statt die Altdaten umzuschreiben. Nur tatsaechlich vorhandene Schluessel
 * landen im Ergebnis, damit `undefined`-Felder nicht als „vorhanden" durchgereicht werden.
 */
function bezugsIds(details: Record<string, unknown> | null | undefined): Partial<
  Pick<AuftragVerlaufEintrag, "invoiceId" | "paymentId" | "messageId" | "costAcceptanceId">
> {
  const ids: Partial<Pick<AuftragVerlaufEintrag, "invoiceId" | "paymentId" | "messageId" | "costAcceptanceId">> = {};
  const invoiceId = detailId(details, "invoiceId", "invoice_id");
  if (invoiceId) ids.invoiceId = invoiceId;
  const paymentId = detailId(details, "paymentId", "payment_id");
  if (paymentId) ids.paymentId = paymentId;
  const messageId = detailId(details, "messageId", "message_id");
  if (messageId) ids.messageId = messageId;
  const costAcceptanceId = detailId(details, "costAcceptanceId", "cost_acceptance_id");
  if (costAcceptanceId) ids.costAcceptanceId = costAcceptanceId;
  return ids;
}

/**
 * A4: Wohin fuehrt der Klick auf diese Zeile? `null` = nirgendwohin, die Zeile bleibt unveraendert
 * nicht klickbar (Antoines Auflage: kein toter Klick).
 *
 * Reine Funktion, damit die Zuordnung Zeile -> Ziel testbar ist, ohne die Karte zu rendern
 * (`test/auftrag-verlauf.test.ts`). Die Karte baut daraus ein `<Link to={ziel.href}>`.
 */
export function verlaufZiel(eintrag: AuftragVerlaufEintrag, eventId: string): AuftragVerlaufZiel | null {
  // Versions-Zeilen oeffnen den `VersionVorschauDrawer`, Eckdaten-Sammelzeilen (PR4) den
  // `FeldVersionenDrawer` — beide auf derselben Seite, kein eigenes Ziel.
  if (eintrag.art === "version" || eintrag.art === "eckdaten") return null;
  if (eintrag.emailId || eintrag.messageId) {
    return { href: posteingangPfad(eventId), label: "Nachricht im Posteingang öffnen" };
  }
  if (eintrag.invoiceId) return { href: abrechnungPfad(eventId), label: "Rechnung in der Abrechnung öffnen" };
  if (eintrag.paymentId) return { href: abrechnungPfad(eventId), label: "Zahlung in der Abrechnung öffnen" };
  if (eintrag.costAcceptanceId) return { href: abrechnungPfad(eventId), label: "Kostenübernahme in der Abrechnung öffnen" };
  return null;
}

/**
 * Kriterium 3: läuft dieselbe Aktion auf demselben Datensatz in einer Kette mit je <= 60s Abstand,
 * bleibt nur der LETZTE Eintrag der Kette übrig (aktueller Stand). Erwartet aufsteigend
 * sortierbare Eingabe, sortiert defensiv selbst.
 */
function fasseBurstsZusammen(entries: AuftragVerlaufActivityEntry[]): AuftragVerlaufActivityEntry[] {
  const sortiert = [...entries].sort((a, b) => zeitMs(a.createdAt) - zeitMs(b.createdAt));
  const ergebnis: AuftragVerlaufActivityEntry[] = [];
  for (const e of sortiert) {
    const letzter = ergebnis[ergebnis.length - 1];
    const eId = entityId(e.details);
    const gehoertZusammen =
      letzter !== undefined &&
      eId !== null &&
      letzter.action === e.action &&
      entityId(letzter.details) === eId &&
      zeitMs(e.createdAt) - zeitMs(letzter.createdAt) <= BURST_FENSTER_MS;
    if (gehoertZusammen) {
      ergebnis[ergebnis.length - 1] = e; // letzter Stand ersetzt den vorherigen der Kette
    } else {
      ergebnis.push(e);
    }
  }
  return ergebnis;
}

function klassifiziere(action: string): AuftragVerlaufArt {
  if (action === "inquiry.created" || action === "inquiry.manuell_uebernommen") return "anfrage";
  if (action === "offer.aenderungswunsch") return "aenderungswunsch";
  if (action === "offer.chosen" || action === "offer.telefonisch_angenommen") return "annahme";
  if (action === "offer.declined") return "ablehnung";
  if (action.startsWith("invoice.") || action.startsWith("rechnung.")) return "rechnung";
  if (action.startsWith("payment.")) return "zahlung";
  if (action.startsWith("cost_acceptance.")) return "kostenuebernahme";
  return "status";
}

/**
 * Titel/Icon je Aktionscode — dieselbe vollständige Tabelle wie die bisherige Aktivität-Karte
 * (WP0). Ein unbekannter Code fällt defensiv auf den Rohcode zurück, nie auf einen Crash.
 *
 * Antoine 13.09.2026: „Status geändert sagt niemandem etwas. Was genau wurde von wem wann
 * geändert." — `event.status_changed` trägt `{from, to}` bereits in `details` (events.ts,
 * logActivity-Aufruf beim Statuswechsel), wurde bisher aber nie gerendert. Wo verfügbar, ersetzt
 * der konkrete Wechsel den generischen Titel, statt ihn nur zu ergänzen — „von wem/wann" steht
 * an der Zeile bereits (Akteur/Zeitstempel), hier fehlte ausschließlich das „was".
 */
const OFFER_FELD_LABEL: Record<string, string> = { title: "Titel", guestCount: "Gästezahl" };

function bezeichnung(action: string, details?: Record<string, unknown> | null): { titel: string; icon: string } {
  const meta = ACTIVITY_LABEL[action] ?? { label: action, icon: "history" };
  // sql/742: „Rechnung nicht erstellt: Rechnungsadresse fehlt“ statt nur der allgemeinen Überschrift.
  if (action === "rechnung.nicht_erstellt" || action === "rechnung.uebersprungen") {
    return { titel: rechnungNichtErstelltText(action, details), icon: meta.icon };
  }
  if (action === "event.status_changed" && details) {
    const from = typeof details.from === "string" ? details.from : null;
    const to = typeof details.to === "string" ? details.to : null;
    if (from && to) {
      return { titel: `${eventStatusMeta(from).label} → ${eventStatusMeta(to).label}`, icon: meta.icon };
    }
  }
  // details.changes: [{field, oldValue, newValue}] — dieselbe Form wie der Undo-Kompensations-
  // Deskriptor (routes/offers.ts), hier separat aus `details` gelesen statt ueber eine zweite
  // Abfrage der Kompensationstabelle (die ist NUR fuer den Undo-Mechanismus gedacht).
  if (action === "offer.title_or_guest_count_changed" && Array.isArray(details?.changes)) {
    const teile = (details.changes as unknown[])
      .filter((c): c is { field: string; oldValue: unknown; newValue: unknown } => typeof c === "object" && c !== null && "field" in c)
      .map((c) => `${OFFER_FELD_LABEL[c.field] ?? c.field}: ${c.oldValue ?? "–"} → ${c.newValue ?? "–"}`);
    if (teile.length > 0) return { titel: teile.join(", "), icon: meta.icon };
  }
  if (action === "event.cancelled_mit_kostenuebernahme" && details) {
    const e = KOSTENUEBERNAHME_ENTSCHEIDUNG_LABEL[details.entscheidung as KostenuebernahmeEntscheidung];
    const grund = typeof details.grund === "string" ? details.grund : null;
    if (e) return { titel: `${meta.label}: ${e}${grund ? ` — „${grund}“` : ""}`, icon: meta.icon };
  }
  // KÜ V2 (S1): „KÜ V2 signiert“, „KÜ V1 ersetzt durch V2“, „… wieder geöffnet (Anlass: Gastwunsch)“.
  const kueV = (n: unknown) => (typeof n === "number" ? `V${n}` : null);
  if (action === "cost_acceptance.signed" && kueV(details?.version_no)) {
    return { titel: `Kostenübernahme ${kueV(details?.version_no)} signiert`, icon: meta.icon };
  }
  if (action === "cost_acceptance.superseded" && kueV(details?.version_no) && kueV(details?.superseded_by_version_no)) {
    return { titel: `Kostenübernahme ${kueV(details?.version_no)} ersetzt durch ${kueV(details?.superseded_by_version_no)}`, icon: meta.icon };
  }
  if (action === "offer.amendment_discarded" && kueV(details?.gueltige_version_no)) {
    const weg = kueV(details?.verworfene_version_no);
    return { titel: `Änderung verworfen${weg ? ` (${weg} widerrufen)` : ""} – Kostenübernahme ${kueV(details?.gueltige_version_no)} gilt weiter`, icon: meta.icon };
  }
  if (action === "cost_acceptance.requested" && kueV(details?.version_no)) {
    return { titel: `Kostenübernahme ${kueV(details?.version_no)} angefordert`, icon: meta.icon };
  }
  if (action === "offer.reopened_for_amendment" && typeof details?.anlass === "string" && details.anlass in AENDERUNGS_ANLASS_TEXT) {
    return { titel: `${meta.label} (Anlass: ${AENDERUNGS_ANLASS_TEXT[details.anlass as keyof typeof AENDERUNGS_ANLASS_TEXT]})`, icon: meta.icon };
  }
  // Sackgassen-Ausweg (04.11.2026): „Gültigkeit verlängert: bis 18.11.2026“ und der Vermerk „trotz Ablauf“ bei der Zusage.
  if (action === "offer.gueltigkeit_verlaengert" && typeof details?.bis === "string") {
    // Zurücknahme nach einer misslungenen telefonischen Zusage: ehrlich als „zurückgesetzt“ benennen, nicht als Verlängerung.
    if (details.zurueckgesetzt === true) {
      return { titel: `Gültigkeit zurückgesetzt: bis ${new Date(`${details.bis}T12:00:00`).toLocaleDateString("de-DE")}`, icon: meta.icon };
    }
    return { titel: `${meta.label}: bis ${new Date(`${details.bis}T12:00:00`).toLocaleDateString("de-DE")}`, icon: meta.icon };
  }
  if (action === "offer.telefonisch_angenommen" && details?.trotz_ablauf === true) {
    const seit = typeof details.gueltig_bis === "string" ? ` (Angebot war seit ${new Date(`${details.gueltig_bis}T12:00:00`).toLocaleDateString("de-DE")} abgelaufen)` : " (Angebot war abgelaufen)";
    return { titel: `${meta.label} – trotz Ablauf der Gültigkeit${seit}`, icon: meta.icon };
  }
  // „Altes Angebot übernehmen“ (29.09.2026): „Angebot aus V-2026-012 übernommen“.
  if (action === "offer.uebernommen_aus_vorgang" && typeof details?.quellNummer === "string") {
    return { titel: `Angebot aus ${details.quellNummer} übernommen`, icon: meta.icon };
  }
  // „Verschieben“ (26.09.2026): „Terminvorschlag gesendet: Sa., 12.12.2026, So., 13.12.2026“.
  if (action === "event.terminvorschlag_gesendet" && Array.isArray(details?.termine)) {
    const termine = (details.termine as unknown[])
      .filter((t): t is string => typeof t === "string" && /^\d{4}-\d{2}-\d{2}$/.test(t))
      .map((t) => new Date(`${t}T00:00:00.000Z`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }));
    if (termine.length > 0) return { titel: `${meta.label}: ${termine.join(", ")}`, icon: meta.icon };
  }
  return { titel: meta.label, icon: meta.icon };
}

interface VersionRohdaten {
  version: number;
  activity?: AuftragVerlaufActivityEntry;
  history?: AuftragVerlaufHistoryEntry;
}

/** Baut je Angebotsversion GENAU EINE Zeile aus `offer_history` (Wahrheit für Existenz + Anzahl
 *  Positionen) und — wenn im 200er-Activity-Fenster noch vorhanden — der passenden
 *  `offer.sent`/`offer.resent`-Zeile (Wahrheit für Akteur und "gesendet" vs. "erneut gesendet"). */
function baueVersionsZeilen(
  historyEntries: AuftragVerlaufHistoryEntry[],
  versionActivity: Map<number, AuftragVerlaufActivityEntry>,
): AuftragVerlaufEintrag[] {
  const versionen = new Map<number, VersionRohdaten>();
  for (const h of historyEntries) {
    versionen.set(h.version, { version: h.version, history: h, activity: versionActivity.get(h.version) });
  }
  // Verwaiste Activity-Zeile ohne History-Gegenstueck (praktisch sollte das nicht vorkommen -
  // offer_history entsteht in derselben Transaktion wie logActivity("offer.sent"/"offer.resent"))
  // defensiv trotzdem ergaenzen, statt sie stillschweigend zu verlieren.
  for (const [version, activity] of versionActivity) {
    if (!versionen.has(version)) versionen.set(version, { version, activity });
  }

  const zeilen: AuftragVerlaufEintrag[] = [];
  for (const v of versionen.values()) {
    // Regel "Altdaten ergaenzen" (Plan-Risiko, Activity-Limit 200): faellt die Sende-Aktivitaet
    // aus dem Fenster, bleibt `offer_history.sent_at` der Zeitstempel dieser Zeile.
    const zeitpunkt = v.activity?.createdAt ?? v.history?.sentAt ?? null;
    if (!zeitpunkt) continue; // ohne Zeitstempel keine Zeile - Kriterium 1 laesst nichts anderes zu
    const istResend = v.activity?.action === "offer.resent";
    // „Zusagen mit neuer Fassung“: festgehalten ohne Mail - der Verlauf sagt nicht „gesendet“.
    const festgehalten = v.activity?.action === "offer.fassung_festgehalten";
    const { titel, icon } = bezeichnung(festgehalten ? "offer.fassung_festgehalten" : istResend ? "offer.resent" : "offer.sent");
    const hatPositionen = v.history
      ? v.history.optionsSnapshot.every((o) => Array.isArray(o.items) && Array.isArray(o.days))
      : undefined;
    zeilen.push({
      key: `version-${v.version}`,
      art: "version",
      zeitpunkt,
      titel: `${titel} · v${v.version}`,
      icon,
      version: v.version,
      hatPositionen,
    });
  }
  return zeilen;
}

/**
 * Kriterium 2: greedy Näherungs-Abgleich ausgehende Mail ↔ Versions-Zeile über die Zeitnähe
 * (kein Fremdschlüssel vorhanden, s. Modul-Kommentar). Kleinste Zeitdifferenz zuerst zuordnen,
 * damit bei mehreren Kandidaten nicht zufällig die falsche Mail an die falsche Version hängt;
 * jede Version und jede Mail wird höchstens einmal verbraucht.
 */
function ordneMailsDenVersionenZu(
  emails: AuftragVerlaufEmailEntry[],
  versionsZeilen: AuftragVerlaufEintrag[],
): { proVersion: Map<string, AuftragVerlaufEmailEntry>; unabhaengig: AuftragVerlaufEmailEntry[] } {
  const ausgehend = emails.filter((m) => m.direction === "outbound");
  const eingehend = emails.filter((m) => m.direction !== "outbound");

  const paare = versionsZeilen
    .flatMap((z) => ausgehend.map((m) => ({ z, m, delta: Math.abs(zeitMs(m.receivedAt) - zeitMs(z.zeitpunkt)) })))
    .filter((p) => p.delta <= MAIL_ZUORDNUNGS_FENSTER_MS)
    .sort((a, b) => a.delta - b.delta);

  const proVersion = new Map<string, AuftragVerlaufEmailEntry>();
  const belegteMails = new Set<string>();
  for (const p of paare) {
    if (proVersion.has(p.z.key) || belegteMails.has(p.m.id)) continue;
    proVersion.set(p.z.key, p.m);
    belegteMails.add(p.m.id);
  }
  const unabhaengig = [...eingehend, ...ausgehend.filter((m) => !belegteMails.has(m.id))];
  return { proVersion, unabhaengig };
}

function mailZeile(m: AuftragVerlaufEmailEntry): AuftragVerlaufEintrag {
  return {
    key: `mail-${m.id}`,
    art: "mail",
    zeitpunkt: m.receivedAt,
    titel: m.direction === "outbound" ? `E-Mail an ${m.toAddr ?? "?"}` : `E-Mail von ${m.fromAddr}`,
    detail: m.subject ?? undefined,
    icon: m.direction === "outbound" ? "call_made" : "call_received",
    emailId: m.id,
  };
}

/** Plan: "Änderungswunsch → „daraus wurde v{n}"" — die naechste Version, die NACH dem
 *  Änderungswunsch entstand. Ohne spätere Version bleibt `folgeVersion`/`detail` unbelegt: eine
 *  noch unbeantwortete Bitte wird nicht erfunden. */
function ergaenzeFolgeVersion(eintraege: AuftragVerlaufEintrag[]): void {
  const versionen = eintraege.filter((e): e is AuftragVerlaufEintrag & { version: number } => e.art === "version" && typeof e.version === "number");
  for (const e of eintraege) {
    if (e.art !== "aenderungswunsch") continue;
    const folge = versionen
      .filter((v) => zeitMs(v.zeitpunkt) > zeitMs(e.zeitpunkt))
      .sort((a, b) => zeitMs(a.zeitpunkt) - zeitMs(b.zeitpunkt))[0];
    if (folge) {
      e.folgeVersion = folge.version;
      e.detail = `Daraus wurde v${folge.version}`;
    }
  }
}

export function leiteAuftragsVerlaufAb({
  activity,
  history,
  emails,
  payments,
}: {
  activity: AuftragVerlaufActivityEntry[];
  history: AuftragVerlaufHistoryEntry[];
  emails: AuftragVerlaufEmailEntry[];
  /** Optional: die Zahlungen des Vorgangs — liefern die Art (Anzahlung/Restzahlung) zu `details.paymentId`. */
  payments?: { id: string; paymentType: string }[];
}): AuftragVerlaufEintrag[] {
  const zahlungsArt = new Map((payments ?? []).map((p) => [p.id, p.paymentType]));
  const dedupliziert = fasseBurstsZusammen(activity);

  // offer.sent/offer.resent werden NICHT generisch klassifiziert - sie fliessen in die
  // Versions-Zeile (Kriterium 2), damit sie nicht zusaetzlich als eigene Status-Zeile auftauchen.
  const versionActivity = new Map<number, AuftragVerlaufActivityEntry>();
  const sonstige: AuftragVerlaufActivityEntry[] = [];
  for (const e of dedupliziert) {
    const version = typeof e.details?.version === "number" ? e.details.version : null;
    if ((e.action === "offer.sent" || e.action === "offer.resent" || e.action === "offer.fassung_festgehalten") && version !== null) {
      const bisher = versionActivity.get(version);
      if (!bisher || zeitMs(e.createdAt) > zeitMs(bisher.createdAt)) versionActivity.set(version, e);
      continue;
    }
    // sql/742: reiner Marker fuer die Glocke, die Rechnung selbst steht als invoice.created da.
    if (e.action === "rechnung.erstellt") continue;
    sonstige.push(e);
  }

  const versionsZeilen = baueVersionsZeilen(history, versionActivity);
  const { proVersion, unabhaengig } = ordneMailsDenVersionenZu(emails, versionsZeilen);
  for (const zeile of versionsZeilen) {
    const mail = proVersion.get(zeile.key);
    if (mail?.toAddr) zeile.detail = `An ${mail.toAddr}`;
  }

  const klassifizierteZeilen: AuftragVerlaufEintrag[] = sonstige.flatMap((e) => {
    // 19.10.2026: „Zahlung eingegangen“ wird aus den Details sprechend (Anzahlung/Restzahlung mit
    // Betrag); bei Deckung kommt ein Abschluss-Eintrag dazu — beides aus DEMSELBEN Protokolleintrag.
    const zahlung = e.action === "payment.succeeded" ? zahlungEingegangenText(e.details, zahlungsArt.get(detailId(e.details, "paymentId", "payment_id") ?? "")) : null;
    const { titel, icon } = zahlung ?? bezeichnung(e.action, e.details);
    const zeile: AuftragVerlaufEintrag = {
      key: `activity-${e.id}`,
      art: klassifiziere(e.action),
      zeitpunkt: e.createdAt,
      titel,
      ...(zahlung?.detail ? { detail: zahlung.detail } : {}),
      icon,
      ...(zahlung ? { erfolg: true } : {}),
      // A4: die IDs aus `details` durchreichen statt sie wegzuwerfen (s. Typ-Kommentar oben).
      ...bezugsIds(e.details),
    };
    if (!zahlung?.abschluss) return [zeile];
    // Eine Millisekunde später, damit der Abschluss in der absteigenden Liste über der Zahlung steht.
    const abschluss: AuftragVerlaufEintrag = {
      ...zeile,
      key: `activity-${e.id}-abschluss`,
      zeitpunkt: new Date(zeitMs(e.createdAt) + 1).toISOString(),
      titel: zahlung.abschluss.titel,
      icon: zahlung.abschluss.icon,
    };
    return [zeile, abschluss];
  });

  const mailZeilen = unabhaengig.map(mailZeile);

  const alle = [...versionsZeilen, ...klassifizierteZeilen, ...mailZeilen];
  ergaenzeFolgeVersion(alle);

  return alle.sort((a, b) => zeitMs(b.zeitpunkt) - zeitMs(a.zeitpunkt));
}
